import { Button, Collapse, Group, Paper } from "@mantine/core";
import { IconCaretDown, IconCaretRight } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiWithoutCamelization } from "../../../../../api";
import { CompanyCell } from "../../../../../mantine/mrt/components/cell/CompanyCell";
import { useToast } from "../../../../../mantine/notifications/useToast";
import urls from "../../../../../urls";
import type { Person } from "../../../../../utils/backend-types";
import type { ChangeableFieldData } from "../../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ChangeableFieldWidget } from "../../../../ChangeableFieldWidget/ChangeableFieldWidget";
import { ContentWithSkeleton } from "../../../../ContentWithSkeleton/ContentWithSkeleton";
import { LoadOrError } from "../../../../LoadOrError/LoadOrError";
import type { OperationPeriod } from "../../../../PeriodBasedComponents/ChangeableOperator/ChangeableOperator";
import { ChangeableOperatorHistory } from "../../../../PeriodBasedComponents/ChangeableOperator/ChangeableOperatorHistory";
import { convertChangeableFieldDataToOperationPeriods } from "../../../../PeriodBasedComponents/ChangeableOperator/utils/convertChangeableFieldDataToOperationPeriods";
import { convertOperatorsToChangeableFieldData } from "../../../../PeriodBasedComponents/ChangeableOperator/utils/convertOperatorsToChangeableFieldData";

interface ConsumerOperatorFieldProps {
  confirmationCheckUrl: string;
  consumerId: number;
  persons: Array<Person>;
  value: number | null;
  onChange: (personId: number) => void;
}

const CHANGEABLE_OPTIONS = {
  addButtonText: "neuen Mieter eintragen",
  startDateHelpText:
    "Bitte erfassen Sie melderelevante Mieterwechsel nach dem 01.01.2020 hier.",
  endDatePlaceholder: "Aktuell",
  deleteTooltipText: "Mieterwechsel löschen",
  saveButtonText: "Mieterwechsel übernehmen"
};

function ConsumerOperatorField({
  confirmationCheckUrl,
  consumerId,
  persons,
  value,
  onChange
}: ConsumerOperatorFieldProps) {
  const { showToast, showErrorToast } = useToast();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const queryClient = useQueryClient();

  const {
    isLoading: isOperatorsLoading,
    error: operatorsError,
    data: operators
  } = useQuery({
    queryKey: ["operation-periods", { componentId: consumerId }],
    queryFn: () => fetchOperators(consumerId),
    refetchInterval: false,
    refetchOnWindowFocus: false
  });
  const { data: confirmationCheckText } = useQuery({
    queryKey: [
      "changeable-operator-confirmation-check",
      { componentId: consumerId, confirmationCheckUrl }
    ],
    queryFn: () => fetchConfirmationCheckText(confirmationCheckUrl),
    refetchInterval: false,
    refetchOnWindowFocus: false
  });

  const currentCompanyName = persons.find(
    (person) => person.id === value
  )?.name;
  const suggestedChangeableOperators =
    convertOperatorsToChangeableFieldData(operators);

  function handleClickHistoryButton() {
    setIsEditOpen(false);
    setIsHistoryOpen((open) => !open);
  }

  function handleClickEditButton() {
    setIsHistoryOpen(false);
    setIsEditOpen((open) => !open);
  }

  function handleSaveTenantChanges(
    changeableFieldData: Array<ChangeableFieldData<number | null>>
  ) {
    const tenantChanges =
      convertChangeableFieldDataToOperationPeriods(changeableFieldData);

    for (const text of confirmationCheckText ?? []) {
      showToast({ type: "info", message: text });
    }

    return apiWithoutCamelization
      .post(urls.api.operationPeriods(consumerId), tenantChanges)
      .then(() => {
        const lastTenantChange = tenantChanges[tenantChanges.length - 1];

        onChange(lastTenantChange.company);
        setIsEditOpen(false);
        queryClient.invalidateQueries({
          queryKey: ["operation-periods", { componentId: consumerId }]
        });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === "persons"
        });
      })
      .catch((error) => {
        showErrorToast(error);
      });
  }

  return (
    <div className="ConsumerOperatorField">
      <Group mb="xs">
        <Button
          flex={1}
          justify="flex-start"
          leftSection={
            isHistoryOpen ? (
              <IconCaretDown strokeWidth="1" />
            ) : (
              <IconCaretRight strokeWidth="1" />
            )
          }
          variant="default"
          onClick={handleClickHistoryButton}
        >
          <ContentWithSkeleton>{currentCompanyName}</ContentWithSkeleton>
        </Button>
        <Button
          color="primary"
          disabled={isEditOpen}
          onClick={handleClickEditButton}
        >
          Mieterwechsel bearbeiten
        </Button>
      </Group>

      <Collapse expanded={isHistoryOpen}>
        {operators ? (
          <ChangeableOperatorHistory
            companies={persons}
            operators={operators}
          />
        ) : (
          <Paper bd="1px solid neutral.3" shadow="none">
            <LoadOrError error={operatorsError} loading={isOperatorsLoading} />
          </Paper>
        )}
      </Collapse>

      <Collapse expanded={isEditOpen}>
        <Paper bd="1px solid neutral.3" shadow="none">
          <LoadOrError error={operatorsError} loading={isOperatorsLoading}>
            {suggestedChangeableOperators && (
              <ChangeableFieldWidget
                confirmationOnSaveCancellable={!confirmationCheckText?.length}
                dataName="Mieterwechsel"
                initialValues={suggestedChangeableOperators}
                options={CHANGEABLE_OPTIONS}
                valueColumns={[
                  {
                    header: "Unternehmen*",
                    accessorKey: "value",
                    Cell: (props, onChangeCell) => (
                      <CompanyCell
                        companies={persons}
                        companyId={props.cell.getValue<number | null>()}
                        value={props.cell.getValue<number | null>()}
                        onChange={(newValue) =>
                          onChangeCell(props.row.index, newValue)
                        }
                      />
                    )
                  }
                ]}
                onCancel={() => setIsEditOpen(false)}
                onDone={handleSaveTenantChanges}
              />
            )}
          </LoadOrError>
        </Paper>
      </Collapse>
    </div>
  );
}

async function fetchOperators(consumerId: number) {
  const response = await apiWithoutCamelization.get<Array<OperationPeriod>>(
    urls.api.operationPeriods(consumerId)
  );
  return response.data;
}

async function fetchConfirmationCheckText(confirmationCheckUrl: string) {
  const response = await apiWithoutCamelization.get<
    { message: string } | undefined
  >(confirmationCheckUrl);

  return response.data?.message ? [response.data.message] : [];
}

export { ConsumerOperatorField };
